import React, { useMemo, useState } from 'react';
import { Link, router } from '@inertiajs/react';
import Layout from '../../../components/Layout.jsx';

const inputClass =
  "block w-full rounded-lg border border-gray-200 bg-white px-3.5 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 focus:outline-none transition";

const labelClass = "block text-sm font-semibold text-gray-700 mb-1.5";

const CEU_OPTIONS = [1, 2, 3, 4];

const DEFAULT_FACILITY_TYPES = ['NHA', 'RCAL', 'ALF', 'HCBS'];

export default function SuperadminAdminCoursesEdit({ course = {}, facets = {}, sources = [] }) {
  const [name, setName] = useState(course.name || '');
  const [ceuCreditHours, setCeuCreditHours] = useState(
    course.ceu_credit_hours === '' || course.ceu_credit_hours == null ? '' : String(course.ceu_credit_hours)
  );
  const [facilityTypes, setFacilityTypes] = useState(course.license_types || []);
  const [role, setRole] = useState(course.role || '');
  const [description, setDescription] = useState(course.description || '');
  const [sourceIds, setSourceIds] = useState(course.source_ids || []);
  const [sourceQuery, setSourceQuery] = useState('');
  const [additionalNotes, setAdditionalNotes] = useState(course.additional_notes || '');
  const [submitting, setSubmitting] = useState(false);

  const facilityTypeOptions = useMemo(() => {
    const merged = new Set([...DEFAULT_FACILITY_TYPES, ...(facets.facility_types || [])]);
    return Array.from(merged);
  }, [facets.facility_types]);

  const toggleFacilityType = (value) => {
    setFacilityTypes((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const toggleSource = (id) => {
    setSourceIds((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]));
  };

  const filteredSources = useMemo(() => {
    const q = sourceQuery.trim().toLowerCase();
    if (!q) return sources;
    return sources.filter(
      (s) =>
        (s.title || '').toLowerCase().includes(q) ||
        (s.author_organization || '').toLowerCase().includes(q)
    );
  }, [sources, sourceQuery]);

  const selectedSources = useMemo(
    () => sources.filter((s) => sourceIds.includes(s.id)),
    [sources, sourceIds]
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitting(true);
    const payload = {
      admin_course: {
        name: name.trim(),
        ceu_credit_hours: ceuCreditHours,
        license_types: facilityTypes,
        role: role.trim(),
        description,
        source_ids: sourceIds,
        additional_notes: additionalNotes,
      },
    };
    router.patch(`/superadmin/admin_courses/${course.id}`, payload, {
      onFinish: () => setSubmitting(false),
    });
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-6 sm:p-8 space-y-6">
        <div>
          <Link
            href="/superadmin/admin_courses"
            className="text-sm text-gray-500 hover:text-gray-700 inline-flex items-center gap-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
            All courses
          </Link>
          <div className="mt-3 flex w-fit items-center gap-1.5 px-2.5 py-1 rounded-full bg-purple-50 text-purple-700 border border-purple-200 text-xs font-medium">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            Superadmin
          </div>
          <h1 className="mt-2 text-3xl font-bold tracking-tight text-gray-900">Edit Course</h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm space-y-6">
                <div>
                  <label htmlFor="name" className={labelClass}>Title <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    autoFocus
                    placeholder="e.g. Resident Rights & Dignity"
                    className={inputClass}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="ceu_credit_hours" className={labelClass}>CEU Credit Hours</label>
                    <select
                      id="ceu_credit_hours"
                      value={ceuCreditHours}
                      onChange={(e) => setCeuCreditHours(e.target.value)}
                      className={inputClass}
                    >
                      <option value="">Select hours…</option>
                      {CEU_OPTIONS.map((h) => (
                        <option key={h} value={h}>{h}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="role" className={labelClass}>Role</label>
                    <select
                      id="role"
                      value={role}
                      onChange={(e) => setRole(e.target.value)}
                      className={inputClass}
                    >
                      <option value="">Select role…</option>
                      {(facets.roles || []).map((r) => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                      {!(facets.roles || []).includes('Administrator') && (
                        <option value="Administrator">Administrator</option>
                      )}
                      {!(facets.roles || []).includes('Caregiver') && (
                        <option value="Caregiver">Caregiver</option>
                      )}
                    </select>
                  </div>
                </div>

                <div>
                  <span className={labelClass}>Facility Types</span>
                  <div className="flex flex-wrap gap-2">
                    {facilityTypeOptions.map((ft) => {
                      const checked = facilityTypes.includes(ft);
                      return (
                        <button
                          key={ft}
                          type="button"
                          onClick={() => toggleFacilityType(ft)}
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition cursor-pointer ${
                            checked
                              ? 'bg-indigo-600 text-white border-indigo-600'
                              : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          {checked && (
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                          {ft}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm space-y-6">
                <div>
                  <label htmlFor="description" className={labelClass}>Course Description</label>
                  <textarea
                    id="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={6}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label htmlFor="additional_notes" className={labelClass}>Additional Notes</label>
                  <textarea
                    id="additional_notes"
                    value={additionalNotes}
                    onChange={(e) => setAdditionalNotes(e.target.value)}
                    rows={4}
                    className={inputClass}
                  />
                </div>
              </div>
            </div>

            <div className="lg:col-span-1">
              <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm lg:sticky lg:top-6">
                <div className="flex items-baseline justify-between mb-3">
                  <span className={labelClass + " !mb-0"}>Sources</span>
                  <span className="text-xs text-gray-400">{sourceIds.length} selected</span>
                </div>

                {selectedSources.length > 0 && (
                  <div className="mb-3 flex flex-wrap gap-1.5">
                    {selectedSources.map((s) => (
                      <span
                        key={s.id}
                        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-indigo-50 text-indigo-700 border border-indigo-200 text-xs font-medium"
                      >
                        {s.title}
                        <button
                          type="button"
                          onClick={() => toggleSource(s.id)}
                          className="hover:text-indigo-900 cursor-pointer"
                          aria-label={`Remove ${s.title}`}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                <input
                  type="text"
                  value={sourceQuery}
                  onChange={(e) => setSourceQuery(e.target.value)}
                  placeholder="Search sources…"
                  className={inputClass}
                />

                <div className="mt-2 max-h-96 overflow-y-auto rounded-lg border border-gray-200 divide-y divide-gray-100">
                  {filteredSources.length === 0 ? (
                    <p className="px-3 py-4 text-sm text-gray-500 text-center">
                      {sources.length === 0
                        ? 'No sources yet.'
                        : 'No sources match.'}
                    </p>
                  ) : (
                    filteredSources.map((s) => {
                      const checked = sourceIds.includes(s.id);
                      return (
                        <label
                          key={s.id}
                          className="flex items-start gap-3 px-3 py-2.5 hover:bg-gray-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleSource(s.id)}
                            className="mt-0.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                          />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-gray-900 truncate">{s.title}</p>
                            {s.author_organization && (
                              <p className="text-xs text-gray-500 truncate">{s.author_organization}</p>
                            )}
                          </div>
                        </label>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-2">
            <Link
              href="/superadmin/admin_courses"
              className="sm:w-auto inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white px-6 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={submitting}
              className="sm:w-auto inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition cursor-pointer"
            >
              {submitting ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
